import { WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { ThemeProvider } from "@mui/material";
import { useAppStateContext } from "@src/appV2/App/useAppState";
import { useSetPreference } from "@src/appV2/redesign/utils/useSetPreference";
import { useSkillsAssessmentFeatureFlags } from "@src/appV2/SkillsAssessment/api/useSkillsAssessmentFeatureFlags";
import { SKILLS_ASSESSMENT_BASE_PATH } from "@src/appV2/SkillsAssessment/paths";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useGetOrientationAlertData } from "../hooks/useGetOrientationAlertData";
import { LASTEST_ORIENTATION_FAILURE_ALERT_EVENT_ID_KEY } from "../hooks/useHasSeenLastestOrientationFailureAlert";
import { formatOrientationDateTime } from "../utils";
import { OrientationFailureAlertBottomSheet } from "./OrientationFailureAlertBottomSheet";
import { OrientationReadyToStartAlertBottomSheet } from "./OrientationReadyToStartAlertBottomSheet";

export function OrientationAlertBottomSheet() {
  const location = useLocation();
  const worker = useDefinedWorker();

  const {
    state: { isAppActive },
  } = useAppStateContext();

  const { isClipboardOrientationEnabled } = useSkillsAssessmentFeatureFlags();
  const {
    orientationAlertType,
    scheduledFor,
    retakeAllowedAfter,
    scheduledEventId,
    isWorkerOnTime,
  } = useGetOrientationAlertData({
    workerId: worker.userId,
  });
  const { mutate: setLastestOrientationFailureAlertEventId } = useSetPreference();
  const failedModalState = useModalState(orientationAlertType === "failed" ? "open" : "closed");
  const { setModalStatus: setFailedModalStatus, modalIsOpen: failedModalIsOpen } = failedModalState;
  const readyToStartModalState = useModalState(
    orientationAlertType === "ready-to-start" ? "open" : "closed"
  );
  const { setModalStatus: setReadyToStartModalStatus, modalIsOpen: readyToStartModalIsOpen } =
    readyToStartModalState;

  useEffect(() => {
    if (orientationAlertType === "failed" && isDefined(scheduledEventId)) {
      setFailedModalStatus("open");
      setReadyToStartModalStatus("closed");
      setLastestOrientationFailureAlertEventId({
        key: LASTEST_ORIENTATION_FAILURE_ALERT_EVENT_ID_KEY,
        value: scheduledEventId,
      });
    } else if (orientationAlertType === "ready-to-start") {
      setFailedModalStatus("closed");
      setReadyToStartModalStatus("open");
    }
  }, [
    orientationAlertType,
    setFailedModalStatus,
    setReadyToStartModalStatus,
    scheduledEventId,
    setLastestOrientationFailureAlertEventId,

    // if isAppActive change we need to show the dialog again
    isAppActive,
  ]);

  /**
   * If the user lands directly in the assessment page, the alert opens in the background
   * even though it is not visible. When they navigate back out of the assessment page,
   * the alert will be shown. We don't want this to happen.
   *
   * This effect closes the alert if it is open and the user is on the assessment page so that
   * when they navigate back out of the assessment page, the alert is not shown.
   */
  useEffect(() => {
    if (
      (failedModalIsOpen || readyToStartModalIsOpen) &&
      location.pathname === SKILLS_ASSESSMENT_BASE_PATH
    ) {
      setFailedModalStatus("closed");
      setReadyToStartModalStatus("closed");
    }
  }, [
    location.pathname,
    failedModalIsOpen,
    readyToStartModalIsOpen,
    setFailedModalStatus,
    setReadyToStartModalStatus,
  ]);

  if (
    // if already on the assessment page, we don't need to show the dialog
    location.pathname === SKILLS_ASSESSMENT_BASE_PATH ||
    // if the worker is not enrolled, we don't need to show the dialog
    ![WorkerStage.ENROLLED, WorkerStage.RESTRICTED, WorkerStage.ONBOARDING].includes(
      worker.stage
    ) ||
    // if the feature is not enabled, we don't need to show the dialog
    !isClipboardOrientationEnabled
  ) {
    return null;
  }

  const scheduledForTimeString = isDefined(scheduledFor)
    ? formatOrientationDateTime(scheduledFor)
    : undefined;
  const retakeAllowedAfterTimeString = isDefined(retakeAllowedAfter)
    ? formatOrientationDateTime(retakeAllowedAfter, { roundTime: true })
    : undefined;

  return (
    <ThemeProvider theme={getTheme()}>
      {isDefined(scheduledForTimeString) && (
        <OrientationReadyToStartAlertBottomSheet
          modalState={readyToStartModalState}
          scheduledForTimeString={scheduledForTimeString}
          isWorkerOnTime={isWorkerOnTime}
        />
      )}
      {isDefined(scheduledForTimeString) && isDefined(retakeAllowedAfterTimeString) && (
        <OrientationFailureAlertBottomSheet
          modalState={failedModalState}
          scheduledForTimeString={scheduledForTimeString}
          retakeAllowedAfterTimeString={retakeAllowedAfterTimeString}
        />
      )}
    </ThemeProvider>
  );
}
